---
title: "Building a Trading Plan and Journal"
slug: trading-plan-and-journal
shortTitle: "Trading Plan & Journal"
description: "Write a one-page trading plan with clear risk, entry and exit rules, then keep a journal that turns your closed trades into numbers you can learn from."
level: intermediate
category: trading
course: intermediate
order: 11
topics: [psychology, trading-strategies]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/trading-plan-and-journal.svg
faqs:
  - q: "How long should a trading plan be?"
    a: "Short enough to read before every session, usually one or two pages. If a rule cannot be answered with a yes or a no, rewrite it until it can."
  - q: "Why keep a journal when the platform already stores my trade history?"
    a: "The history records what happened: prices, fees, swap and the net result. A journal adds why you took the trade, which setup it was, whether you followed your rules and how you felt, which is the part you can actually improve."
  - q: "How many trades do I need before I judge a strategy?"
    a: "There is no magic number, but a handful of trades tells you very little because luck dominates small samples. Many traders wait for at least 30 to 50 trades of the same setup before drawing conclusions, and even then treat the result as a rough estimate."
  - q: "Should I change my plan after a losing streak?"
    a: "First check whether the losses were within what the plan expected and whether you followed it. Losing streaks happen even with a sound approach, so change rules at a scheduled review, one at a time, rather than in the middle of a bad week."
  - q: "Can I test a plan without risking real money?"
    a: "Yes. A demo account lets you follow the plan with virtual funds, and you can journal those trades exactly as you would live ones. Live trading usually feels different, so move to small real positions gradually."
---

Most trading decisions are made at the worst possible moment: with a price moving, a position open and your heart rate up. A **trading plan** moves those decisions to a calm moment. It is a short written document that says what you trade, when you trade, how you get in and out, and how much you are willing to lose. A **trading journal** is the other half: a record of every trade and the reasoning behind it, so you can check whether you followed the plan and whether the plan works.

Neither makes you profitable on its own, and neither removes risk. What they do is turn a series of impulses into something you can measure and improve. This lesson shows what to put in each, with worked numbers in USDT.

<CTA />

<KeyTakeaways>

- **A plan is decisions made in advance.** Markets, setups, entries, exits and risk limits are written down before you trade, not invented mid-trade.
- **Risk rules come first.** A fixed risk per trade, a daily loss limit and a cap on open positions protect your account when your analysis is wrong.
- **A journal records the why.** Prices and profit come from your trade history; the setup, the reason and whether you followed the rules have to come from you.
- **Review with numbers, change one thing at a time.** Win rate, average win and loss, and expectancy show what is working, but small samples can mislead.

</KeyTakeaways>

## Why write anything down?

When you decide on the spot, a loss tempts you to widen your stop, a win tempts you to double your size, and fear of missing out pushes you into moves that have already happened. These are normal reactions, covered in [Trading Psychology Basics](/academy/article/trading-psychology-basics), and a written plan is the simplest defense against them.

A plan also makes your results testable. If you trade differently every day, a losing month tells you nothing. If you trade the same setups with the same risk, the numbers start to show whether the approach has an **edge** (a positive average result over many trades).

## What goes in a trading plan

A useful plan fits on one or two pages and covers seven things:

1. **Markets.** A short list you know well, for example BTC/USDT, GOLD/USDT and EUR/USD.
2. **Time.** When you trade and for how long.
3. **Setups.** The exact conditions for a trade, written so another person could check them.
4. **Entry and exit.** Market or Limit / Stop order, where the stop loss goes, where the take profit goes.
5. **Risk.** How much of your account you risk per trade, per day and across all open positions.
6. **Management.** Whether and when you move the stop, and whether you ever close early.
7. **Review.** When you review your journal and how you decide to change a rule.

The hardest part is writing rules that can actually be checked. Compare these:

<CompareTable caption="Vague rules compared with testable ones">

| Vague rule | Testable rule |
| --- | --- |
| "Buy when it looks strong" | "Buy on the 1h chart when price closes above the previous day's high and the 4h chart is making higher highs" |
| "Don't risk too much" | "Risk 1% of equity per trade; stop trading for the day after a 3% loss" |
| "Cut losers quickly" | "Set the SL when the order is placed; never move it further away" |
| "Take profit when it feels right" | "TP at twice the stop distance, or close at the end of my session" |

</CompareTable>

A testable rule has a yes-or-no answer, which matters because your journal will ask "did I follow the plan?" for every trade.

## Risk rules, worked in USDT

Risk rules are the part of the plan that keeps you in the game when your analysis is wrong, which it often will be. [Position Sizing](/academy/article/position-sizing) covers the method in depth; here is how the numbers fit into a plan.

<Example title="Risk rules for a 5,000 USDT account (illustrative prices)">

- **Risk per trade: 1%.** 5,000 × 0.01 = **50 USDT** maximum loss per trade.
- **Daily loss limit: 3%.** 5,000 × 0.03 = **150 USDT**, so three full losses end the day.
- **Position size from the stop.** You plan to buy BTC/USDT at 60,000 with a stop loss at 59,000, a distance of 1,000. Size = 50 ÷ 1,000 = **0.05 BTC**, a position worth 0.05 × 60,000 = **3,000 USDT**.
- **Margin.** At 10x leverage that position ties up 3,000 ÷ 10 = **300 USDT** of margin. Leverage changes the margin, not the 50 USDT at risk.

</Example>

On {{BRAND}}, you can enter that risk directly. Turn on the **TP / SL** switch in the order panel and type 50 in the **Loss (USDT)** field; the panel shows how far that stop sits from your entry in %. Read **Margin required**, **Fee** and **Value of $1 move** in the order summary before you confirm. Remember that the spread and the fee come on top of the 50 USDT.

<Warning>

Margin on {{BRAND}} is shared across your whole account. Five trades that each risk 1% can all lose together if they move in step, such as several crypto positions in a sharp sell-off. Cap your total open risk, not just the risk per trade, so a bad day never pushes your margin level toward {{STOP_OUT_LEVEL}}. A stop loss fills at market, so in a gap or a fast move the loss can be larger than planned.

</Warning>

## Write your plan in one sitting

<Steps>
<Step title="List your markets and hours">

Pick two to four markets. Open each on the Markets page and note its trading hours, fee and swap.

</Step>
<Step title="Describe one or two setups">

Write each setup as a short checklist: timeframe, the condition that must be true, and the trigger for entry. Give each a name, such as "pullback to support", so you can tag trades with it.

</Step>
<Step title="Set your risk numbers">

Choose your risk per trade, a daily loss limit, a maximum number of open positions and the leverage you will normally use. Using less than {{MAX_LEVERAGE}} is a perfectly normal choice.

</Step>
<Step title="Decide how you manage open trades">

{{BRAND}} has no automatic trailing stop, so if you trail, decide in advance when you will move your stop with **Edit TP / SL** in Open Positions, and never move it further from the price.

</Step>
<Step title="Trial it at small size">

Follow the plan on a demo account or with very small positions for a few weeks, journaling every trade.

</Step>
</Steps>

{/* TODO: verify with platform — demo account availability (Demo button) and that automatic trailing stops are not currently offered. */}

## Keeping a journal that teaches you something

A journal entry has two parts. The facts come from the platform; the context comes from you, ideally written before you know the result.

- **From the trade screen:** date, market, direction, lots, entry price, exit price, why it closed, fees, swap and net result. The **Closed Positions** tab shows **Exit Price**, **Closed**, **Reason** (for example Take profit or Stop loss), **Fees**, **Swap** and **Net PnL** for every trade.
- **From you:** the setup name, why you entered in one sentence, your planned stop and target, the risk in USDT, a chart screenshot at entry, whether you followed the plan (yes or no), and a word on your state of mind.

<Screenshot name="closed-positions-tab" alt="The Closed Positions tab showing exit price, close reason, fees, swap and net PnL" caption="Closed Positions holds the facts for your journal: exit price, reason, fees, swap and net result." />

{/* TODO: verify with platform — whether trade history can be exported; if not, journal entries are copied by hand into a spreadsheet. */}

One extra column makes the journal far more useful: the **R multiple**, your net result divided by the risk you planned. A trade that risked 50 USDT and made 95 USDT is +1.9R. A stop-out that lost 55 USDT after slippage and fees is −1.1R. R lets you compare trades of different sizes on different markets.

<ProTip>

Write the "why" before the trade closes. Once you know the result, memory quietly rewrites the reason. A one-line note at entry is worth more than a paragraph afterwards.

</ProTip>

## Reviewing the journal

Set a fixed time each week to review, and work out a few numbers rather than scrolling through wins and losses.

<Example title="A 20-trade review (illustrative prices)">

- 8 wins averaging +100 USDT and 12 losses averaging −50 USDT.
- Win rate = 8 ÷ 20 = **40%**.
- Net result = (8 × 100) − (12 × 50) = 800 − 600 = **+200 USDT**.
- Expectancy (the average result per trade) = 200 ÷ 20 = **+10 USDT**, which matches (0.4 × 100) − (0.6 × 50) = 40 − 30 = 10.
- Now filter by "followed plan". Say the 5 trades where you broke a rule lost 210 USDT between them. The other 15 made +410 USDT.

</Example>

That split is often the most revealing line in a journal: here the setups may be fine and the problem is discipline. Twenty trades is still a small sample, so treat any conclusion as provisional. [Risk/Reward Ratio and Win Rate](/academy/article/risk-reward-and-win-rate) explains why a 40% win rate can still be profitable and how to read these numbers.

When you change the plan, change one rule at a time and note the date.

## Conclusion

A trading plan is a set of decisions you make while calm so you do not have to make them under pressure. A journal records every trade with its reason and whether you followed the rules. Together they turn your trading into data. Keep the plan short and testable, put risk rules first, journal every trade and review with numbers on a schedule. None of this removes risk, and many traders lose money even with a plan, but it shows you clearly what is happening and why.
